import React, { useMemo } from 'react';
import { displayDate, weekday } from '../utils/dates';

function isWeekend(dateStr) {
  const day = weekday(dateStr);
  return day === 0 || day === 6;
}

function expectedHours(dateStr) {
  return isWeekend(dateStr) ? 0 : 8;
}

export default function TimesheetSummary({ entries, dates, isSubmitted, isApproved, hasOverLimitDay, onSubmit, onReopen, onDeleteJob, onEntryClick, section, favorites }) {
  const showByDate = !section || section === 'bydate';
  const showByJob = !section || section === 'byjob';
  // Build unique jobs, date-by-job pivot, and per-entry details for By Job
  const { jobs, byDateJob, byJobDateEntries, dateTotals, jobTotals, grandTotal } = useMemo(() => {
    const jobMap = new Map();
    const byDateJob = {};
    const byJobDateEntries = {}; // jobKey -> date -> [entry details]

    for (const entry of entries) {
      const date = entry.EntryDate?.split('T')[0];
      const jobKey = `${entry.JobID}|${entry.JobTaskID}`;
      const hrs = parseFloat(entry.Hours) || 0;

      if (!jobMap.has(jobKey)) {
        jobMap.set(jobKey, {
          key: jobKey,
          jobId: entry.JobID,
          jobName: entry.JobName,
          jobTaskId: entry.JobTaskID,
          taskCode: entry.TaskCode,
          taskName: entry.TaskName,
          customerName: entry.CustomerName,
        });
      }

      if (!byDateJob[date]) byDateJob[date] = {};
      byDateJob[date][jobKey] = (byDateJob[date][jobKey] || 0) + hrs;

      // Track individual entries per job/date for By Job section
      if (hrs > 0) {
        if (!byJobDateEntries[jobKey]) byJobDateEntries[jobKey] = {};
        if (!byJobDateEntries[jobKey][date]) byJobDateEntries[jobKey][date] = [];
        byJobDateEntries[jobKey][date].push({
          hours: hrs,
          description: entry.Description || '',
          jobId: entry.JobID,
          jobTaskId: entry.JobTaskID,
          nonBillable: !!entry.NonBillable,
        });
      }
    }

    // Include favorites that have no entries yet
    if (favorites) {
      for (const fav of favorites) {
        const jobKey = `${fav.JobID}|${fav.JobTaskID}`;
        if (!jobMap.has(jobKey)) {
          jobMap.set(jobKey, {
            key: jobKey,
            jobId: fav.JobID,
            jobName: fav.JobName,
            jobTaskId: fav.JobTaskID,
            taskCode: fav.TaskCode,
            taskName: fav.TaskName,
            customerName: fav.CustomerName,
          });
        }
      }
    }

    const jobs = Array.from(jobMap.values());

    // Sort jobs by favorites SortOrder (persisted user preference)
    if (favorites) {
      const favOrder = new Map();
      for (const fav of favorites) {
        favOrder.set(`${fav.JobID}|${fav.JobTaskID}`, fav.SortOrder != null ? fav.SortOrder : Infinity);
      }
      jobs.sort((a, b) => {
        const sa = favOrder.get(a.key) ?? Infinity;
        const sb = favOrder.get(b.key) ?? Infinity;
        if (sa !== sb) return sa - sb;
        return (a.jobName || '').localeCompare(b.jobName || '');
      });
    }

    // Date totals
    const dateTotals = {};
    for (const date of dates) {
      dateTotals[date] = Object.values(byDateJob[date] || {}).reduce((s, h) => s + h, 0);
    }

    // Job totals
    const jobTotals = {};
    for (const job of jobs) {
      jobTotals[job.key] = 0;
      for (const date of dates) {
        jobTotals[job.key] += (byDateJob[date]?.[job.key] || 0);
      }
    }

    const grandTotal = Object.values(jobTotals).reduce((s, h) => s + h, 0);

    return { jobs, byDateJob, byJobDateEntries, dateTotals, jobTotals, grandTotal };
  }, [entries, dates, favorites]);

  // Compute total expected and total variance
  const totalExpected = dates.reduce((s, d) => s + expectedHours(d), 0);
  const totalVariance = grandTotal - totalExpected;

  if (jobs.length === 0) {
    return (
      <div className="empty-state">
        <p>No time entries yet for this period.</p>
        {/* Submit / Reopen */}
        {!isApproved && (
          <div className="action-row" style={{ justifyContent: 'center' }}>
            {isSubmitted ? (
              <button className="btn btn-outline" onClick={onReopen}>Reopen Timesheet</button>
            ) : (
              <button className="btn btn-success" onClick={onSubmit} disabled={hasOverLimitDay}>Submit Timesheet</button>
            )}
          </div>
        )}
        {isApproved && (
          <div style={{ textAlign: 'center', color: '#059669', fontSize: '13px', fontWeight: 600 }}>
            Timesheet Approved
          </div>
        )}
      </div>
    );
  }

  // Show all weekdays (skip weekends with 0 hours)
  const visibleDates = dates.filter((date) => {
    const weekend = isWeekend(date);
    const total = dateTotals[date] || 0;
    // Always show weekdays; only show weekends if they have hours
    return !weekend || total > 0;
  });

  return (
    <div style={{ padding: '0 0 20px' }}>
      {/* By Date Section */}
      {showByDate && <div className="card">
        <div className="card-header">
          <span className="card-title">By Date</span>
          <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>Variance</span>
        </div>
        {visibleDates.map((date) => {
          const dayEntries = entries.filter((e) => e.EntryDate?.split('T')[0] === date && parseFloat(e.Hours) > 0);
          const actual = dateTotals[date] || 0;
          const expected = expectedHours(date);
          const variance = actual - expected;
          const weekend = isWeekend(date);
          return (
            <div key={date} style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px solid var(--gray-200)',
                fontWeight: 600,
                fontSize: '14px',
                opacity: weekend && actual === 0 ? 0.5 : 1,
              }}>
                <span>{displayDate(date, { weekday: true })}</span>
                <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span style={{ color: actual > 24 ? '#dc2626' : undefined, fontWeight: actual > 24 ? 700 : undefined }}>{actual.toFixed(2)} hrs</span>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    minWidth: '50px',
                    textAlign: 'right',
                    color: variance > 0 ? '#059669'
                      : variance < 0 ? '#dc2626'
                      : 'var(--gray-400)',
                  }}>
                    {variance >= 0 ? '+' : ''}{variance.toFixed(2)}
                  </span>
                </span>
              </div>
              {dayEntries.map((entry) => {
                const missingDesc = !entry.Description;
                return (
                  <div key={entry.TimeEntryID} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 0 4px 12px',
                    fontSize: '13px',
                    color: 'var(--gray-600)',
                    cursor: onEntryClick ? 'pointer' : 'default',
                    background: missingDesc ? '#fef9c3' : 'transparent',
                    borderRadius: missingDesc ? '4px' : undefined,
                  }}
                    title={missingDesc ? 'Missing time entry description' : undefined}
                    onClick={onEntryClick ? () => onEntryClick(entry.JobID, entry.JobTaskID, entry.EntryDate?.split('T')[0]) : undefined}
                  >
                    <span>
                      {entry.JobID} - {entry.JobName} / {entry.JobTaskID} – {entry.TaskCode} – {entry.TaskName}
                      {entry.NonBillable ? <span style={{ color: '#b45309', fontSize: '11px', marginLeft: '6px' }}>(Non-Billable)</span> : null}
                    </span>
                    <span style={{ paddingRight: '62px' }}>{parseFloat(entry.Hours).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '10px 0 0',
          borderTop: '2px solid var(--gray-300)',
          fontWeight: 700,
          fontSize: '15px',
        }}>
          <span>Total</span>
          <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <span>{grandTotal.toFixed(2)} hrs</span>
            <span style={{
              fontSize: '12px',
              fontWeight: 600,
              minWidth: '50px',
              textAlign: 'right',
              color: totalVariance > 0 ? '#059669'
                : totalVariance < 0 ? '#dc2626'
                : 'var(--gray-400)',
            }}>
              {totalVariance >= 0 ? '+' : ''}{totalVariance.toFixed(2)}
            </span>
          </span>
        </div>
      </div>}

      {/* By Job Section */}
      {showByJob && <div className="card">
        <div className="card-header">
          <span className="card-title">By Job</span>
        </div>
        {jobs.map((job) => {
          const hasHours = jobTotals[job.key] > 0;
          const isReadOnly = isSubmitted || isApproved;
          const jobDateEntries = byJobDateEntries[job.key] || {};
          // Get dates that have entries for this job
          const jobDates = dates.filter((d) => jobDateEntries[d] && jobDateEntries[d].length > 0);
          return (
          <div key={job.key} style={{ marginBottom: '12px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
              borderBottom: '1px solid var(--gray-200)',
              fontWeight: 600,
              fontSize: '14px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div>{job.jobId} - {job.jobName}</div>
                  <div style={{ fontSize: '12px', fontWeight: 400, color: 'var(--gray-500)' }}>
                    {job.jobTaskId} – {job.taskCode} – {job.taskName} &middot; {job.customerName}
                  </div>
                </div>
                {!isReadOnly && onDeleteJob && (
                  <button
                    onClick={() => {
                      onDeleteJob(job.jobId, job.key.split('|')[1]);
                    }}
                    style={{
                      background: 'none', border: 'none', color: 'var(--danger)',
                      cursor: 'pointer', fontSize: '13px', fontWeight: 500, padding: '2px 6px',
                      whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
              <span style={{ flexShrink: 0, marginLeft: '8px' }}>{jobTotals[job.key].toFixed(2)} hrs</span>
            </div>
            {jobDates.map((date) =>
              jobDateEntries[date].map((ent, idx) => {
                const desc = ent.description || '';
                return (
                  <div key={`${date}-${idx}`} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '8px',
                    padding: '4px 0 4px 12px',
                    fontSize: '13px',
                    color: 'var(--gray-600)',
                    cursor: onEntryClick ? 'pointer' : 'default',
                  }}
                    onClick={onEntryClick ? () => onEntryClick(ent.jobId, ent.jobTaskId, date) : undefined}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '75ch' }}>
                      {displayDate(date)}
                      {desc ? ` — ${desc}` : (
                        <span style={{ color: '#b45309', fontStyle: 'italic' }}> [Missing Time Entry Description]</span>
                      )}
                      {ent.nonBillable ? <span style={{ color: '#b45309', fontSize: '11px', marginLeft: '6px' }}>(Non-Billable)</span> : null}
                    </span>
                    <span style={{ flexShrink: 0 }}>{ent.hours.toFixed(2)}</span>
                  </div>
                );
              })
            )}
          </div>
          );
        })}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '10px 0 0',
          borderTop: '2px solid var(--gray-300)',
          fontWeight: 700,
          fontSize: '15px',
        }}>
          <span>Total</span>
          <span>{grandTotal.toFixed(2)} hrs</span>
        </div>
      </div>}

      {/* Submit / Reopen */}
      {!isApproved && (
        <div className="action-row">
          {isSubmitted ? (
            <button className="btn btn-outline" onClick={onReopen}>Reopen Timesheet</button>
          ) : (
            <button className="btn btn-success" onClick={onSubmit} disabled={hasOverLimitDay}>
              Submit Timesheet
            </button>
          )}
        </div>
      )}
      {isApproved && (
        <div style={{ textAlign: 'center', color: '#059669', fontSize: '13px', padding: '8px 16px', fontWeight: 600 }}>
          Timesheet Approved
        </div>
      )}
      {hasOverLimitDay && !isSubmitted && !isApproved && (
        <div style={{ textAlign: 'center', color: '#dc2626', fontSize: '13px', padding: '0 16px 12px' }}>
          Cannot submit: one or more days exceed 24 hours.
        </div>
      )}

      {/* Submission results are shown globally in TimesheetDetailPage */}
    </div>
  );
}
